/**
 * useLocationCurrency Hook
 * 
 * Provides currency configuration from the active location.
 * Supports multiple currencies with a default, allowing switching between them.
 */

import { useState, useMemo, useCallback } from 'react';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocation } from '@/entities/location';
import {
  CurrencyConfig,
  MonetaryConfig,
  NumberFormatPref,
  getDefaultCurrency
} from '@/entities/location/types';

/**
 * Location number-format preference → a representative Intl locale that
 * produces the desired decimal/thousands separators:
 *   point → 1,234.56   comma → 1.234,56   space → 1 234,56
 */
const NUMBER_FORMAT_LOCALE: Record<NumberFormatPref, string> = {
  point: 'en-US',
  comma: 'de-DE',
  space: 'fr-FR',
};

interface UseLocationCurrencyOptions {
  /** Override monetary config (useful when location is passed as prop) */
  monetaryConfig?: MonetaryConfig;
}

interface UseLocationCurrencyReturn {
  /** Currently selected currency */
  activeCurrency: CurrencyConfig;
  /** Default currency from configuration */
  defaultCurrency: CurrencyConfig;
  /** All available currencies */
  availableCurrencies: CurrencyConfig[];
  /** Whether multiple currencies are available */
  hasMultipleCurrencies: boolean;
  /** Switch to a different currency */
  setCurrency: (currencyCode: string) => void;
  /** Reset to default currency */
  resetToDefault: () => void;
  /** Format an amount using the active currency */
  formatPrice: (amount: number, options?: FormatPriceOptions) => string;
  /** Format a plain number using the location's separator style */
  formatNumber: (value: number, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => string;
  /** Intl locale tag matching the location's number-separator preference (point→en-US, comma→de-DE, space→fr-FR). */
  numberLocale: string;
  /** Convert amount from default currency to active currency */
  convertFromDefault: (amount: number) => number;
  /** Convert amount from active currency to default currency */
  convertToDefault: (amount: number) => number;
}

interface FormatPriceOptions {
  /** Use specific currency instead of active */
  currencyCode?: string;
  /** Show currency code instead of symbol */
  showCode?: boolean;
  /** Minimum fraction digits */
  minimumFractionDigits?: number;
  /** Maximum fraction digits */
  maximumFractionDigits?: number;
}

// Default monetary config for when no location is available
const DEFAULT_MONETARY_CONFIG: MonetaryConfig = {
  currencies: [
    { code: 'EUR', symbol: '€', locale: 'en-US', isDefault: true }
  ],
  timezone: 'UTC',
};

export function useLocationCurrency(
  options: UseLocationCurrencyOptions = {}
): UseLocationCurrencyReturn {
  const { monetaryConfig } = options;
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocation(activeLocationId ?? undefined);

  // Separator style preference (point/comma/space) → Intl locale. Drives
  // decimal/thousands separators for both prices and plain numbers. Defaults
  // to 'comma' (European 0.000,00) — the product default, matching the
  // backend Location default and the DD/MM/YYYY date default.
  const numberFormatPref: NumberFormatPref = activeLocation?.numberFormat ?? 'comma';
  const formatLocale = NUMBER_FORMAT_LOCALE[numberFormatPref];

  const effectiveMonetaryConfig = monetaryConfig
    ?? activeLocation?.monetary
    ?? DEFAULT_MONETARY_CONFIG;

  // Ensure we always have at least one currency
  const currencies = useMemo(() => {
    if (!effectiveMonetaryConfig.currencies || effectiveMonetaryConfig.currencies.length === 0) {
      return DEFAULT_MONETARY_CONFIG.currencies;
    }
    return effectiveMonetaryConfig.currencies;
  }, [effectiveMonetaryConfig.currencies]);

  // Get the default currency
  const defaultCurrency = useMemo(() => {
    return getDefaultCurrency({ ...effectiveMonetaryConfig, currencies });
  }, [effectiveMonetaryConfig, currencies]);

  // Track the currently selected currency
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string>(
    defaultCurrency.code
  );

  // Get the active currency (selected or default if not found)
  const activeCurrency = useMemo(() => {
    const found = currencies.find(c => c.code === selectedCurrencyCode);
    return found ?? defaultCurrency;
  }, [currencies, selectedCurrencyCode, defaultCurrency]);

  // Switch currency
  const setCurrency = useCallback((currencyCode: string) => {
    const exists = currencies.some(c => c.code === currencyCode);
    if (exists) {
      setSelectedCurrencyCode(currencyCode);
    }
  }, [currencies]);

  // Reset to default
  const resetToDefault = useCallback(() => {
    setSelectedCurrencyCode(defaultCurrency.code);
  }, [defaultCurrency.code]);

  // Format price with the active or specified currency
  const formatPrice = useCallback((
    amount: number,
    formatOptions: FormatPriceOptions = {}
  ): string => {
    // `displayPrecision` de la sede decide cuántos decimales se enseñan.
    // Llevaba meses configurable (0, 2 o 3) y sin que nadie lo leyera: aquí
    // había un 2 fijo, así que una sede que lo pusiera a 0 seguía viendo
    // céntimos (PENDINGS #75). Quien pase los suyos explícitamente sigue
    // mandando — hay sitios que necesitan precisión aunque el ticket no.
    const precisionDeLaSede = effectiveMonetaryConfig.displayPrecision ?? 2;
    const {
      currencyCode,
      showCode = false,
      minimumFractionDigits = precisionDeLaSede,
      maximumFractionDigits = precisionDeLaSede,
    } = formatOptions;

    // When an explicit currencyCode is requested, prefer the configured entry
    // but fall back to a bare {code} so arbitrary currencies (bank accounts,
    // Stripe payouts) still format with the RIGHT currency rather than the
    // active one.
    const currency: CurrencyConfig = currencyCode
      ? currencies.find(c => c.code === currencyCode) ?? { code: currencyCode, isDefault: false }
      : activeCurrency;

    // The location's separator preference wins over the currency's own locale
    // so operators get the exact "1,234.56" vs "1.234,56" style they picked.
    try {
      const formatted = new Intl.NumberFormat(formatLocale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits,
        maximumFractionDigits,
        currencyDisplay: showCode ? 'code' : 'symbol',
      }).format(amount);

      return formatted;
    } catch {
      // Fallback for unsupported currencies
      const symbol = currency.symbol ?? currency.code;
      return `${symbol}${amount.toFixed(maximumFractionDigits)}`;
    }
  }, [currencies, activeCurrency, formatLocale]);

  // Format a plain (non-currency) number with the location's separator style.
  const formatNumber = useCallback((
    value: number,
    numberOptions: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
  ): string => {
    const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = numberOptions;
    try {
      return new Intl.NumberFormat(formatLocale, {
        style: 'decimal',
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(value);
    } catch {
      return value.toFixed(maximumFractionDigits);
    }
  }, [formatLocale]);

  // Convert from default currency to active currency
  const convertFromDefault = useCallback((amount: number): number => {
    if (activeCurrency.code === defaultCurrency.code) {
      return amount;
    }
    const rate = activeCurrency.exchangeRate ?? 1;
    return amount * rate;
  }, [activeCurrency, defaultCurrency]);

  // Convert from active currency to default currency
  const convertToDefault = useCallback((amount: number): number => {
    if (activeCurrency.code === defaultCurrency.code) {
      return amount;
    }
    const rate = activeCurrency.exchangeRate ?? 1;
    return rate !== 0 ? amount / rate : amount;
  }, [activeCurrency, defaultCurrency]);

  return {
    activeCurrency,
    defaultCurrency,
    availableCurrencies: currencies,
    hasMultipleCurrencies: currencies.length > 1,
    setCurrency,
    resetToDefault,
    formatPrice,
    formatNumber,
    numberLocale: formatLocale,
    convertFromDefault,
    convertToDefault,
  };
}

export type { UseLocationCurrencyReturn, FormatPriceOptions, CurrencyConfig };
